import { React, Styled } from '@common/3rd';
import { DeskComponentRegistry, CheckableWidget } from '@component';
import { CheckableWidgetState, DeskToggleNumberComponents, DeskToggleNumberProps } from '@my-types';
import { StyledComponents } from '@styles';
import { ErrorReminder, PassReminder } from './input-reminder';

/**
 * 标准桌面计数按钮
 */
class DeskToggleNumber<P extends DeskToggleNumberProps, S extends CheckableWidgetState, C extends DeskToggleNumberComponents> extends CheckableWidget<P, S, C> {
	toggleNumberRef: React.RefObject<any>;
	minValue: number = 0;
	maxValue: number = 999;
	changeDelayHandler?: any;
	timer: any;

	constructor(props: any, context?: any) {
		super(props, context);
		this.toggleNumberRef = React.createRef();
	}
	protected initComponents(): C {
		return {
			Box: Styled.div.attrs({
				'data-widget': 'desk-toggle-number'
			})`
				display: flex;
				flex-grow: 1;
				// grid-template-columns: 1fr 1fr 1fr;
				padding: ${props => props.theme.TOGGLE_VERTICAL_PADDING}px 0;
				height: ${props => props.theme.TOGGLE_HEIGHT}px;
				width: ${props => props.theme.TOGGLE_WIDTH}px;
				outline: none;
				&[data-value=true] > div > span {
					left: calc(100% - ${props => props.theme.TOGGLE_SLIDER_WIDTH + 1}px);
				}
				&[data-value=true]:not([data-readonly=true]) {
					> div {
						border-color: ${props => props.theme.TOGGLE_TRUE_BORDER_COLOR};
						background-color: ${props => props.theme.TOGGLE_TRUE_BACKGROUND_COLOR};
						> span {
							background-color: ${props => props.theme.TOGGLE_TRUE_SLIDER_COLOR};
						}
					}
				}
				> div {
					background-color: ${props => props.theme.TOGGLE_TRUE_SLIDER_COLOR};
					text-align: center;
					width: 28px;
					min-width: 28px;
					height: 28px;
					line-height: 28px;
				}
				> i {
					display: flex;
					line-height: 28px;
    				margin-left: 8px;
					> span {
						position: inherit;
						min-height: 28px;
						line-height: 28px;
						padding: 0px 10px;
						max-width: 400px;
    					overflow: visible;
					}
				}
				> input {
					text-align: center;
					height: 28px;
					line-height: 28px;
					min-width: 60px;
				}
				// 去掉number属性的自带上下按钮
				> input::-webkit-inner-spin-button {
					-webkit-appearance: none !important;
				}
				> input::-webkit-outer-spin-button{
					-webkit-appearance: none !important;
				}
				> input[type="number"]{
					-moz-appearance: textfield;
				}
				${props => this.renderCSS(props.theme)}
			`,
			Text: Styled(StyledComponents.INPUT)`
				line-height: 28px;
				min-width: 50px;
				&[disabled],
				&[readOnly],
				&[readonly] {
					// background-color: transparent; 
				}

				/* 获取焦点的时候, 如果有错误信息, 显示 */
				:focus + i > span {
					opacity: 1;
					animation-name: none;
				}
			`,
		} as C;
	}
	render() {
		const C = this.getComponents();
		return (
			<C.Box>
				{this.renderMinusButton()}
				<C.Text
					type="number"
					onChange={this.onTextChanged}
					onFocus={this.onTextFocus}
					onBlur={this.onTextBlur}
					ref={this.toggleNumberRef}
					readOnly={!this.isEditable()}
					defaultValue={this.getValueFromModel()}
					maxLength={this.getMaxlength()}
				/>
				{this.renderAddButton()}
				{this.renderReminder()}
			</C.Box>
		);
	}
	renderReminder(): JSX.Element | void {
		if (this.isCheckFailOnWhatever()) {
			// 有校验失败信息
			return <ErrorReminder msg={this.getCheckFailMessage()!} />;
		} else if (this.isCheckedAnyway()) {
			// 没有校验信息, 但是曾经校验过
			return <PassReminder />;
		}
	}
	protected onTextChanged = (): void => {
		const value = this.toggleNumberRef.current.value;
		const oldValue = this.getValueFromModel();
		if (value === oldValue) {
			// 没有变化, 什么都不做
			return;
		}
		if (value && Number(value) > this.maxValue) {
			this.handleValueChange(() => this.setValueToModel(this.maxValue).checkData());
		} else {
			this.handleValueChange(() => this.setValueToModel(value).checkData());
		}
	};
	protected onTextFocus = (): void => {
		clearTimeout(this.timer);
		this.toggleNumberRef.current!.select();
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusGained();
		}
	};
	protected onTextBlur = (): void => {
		if (this.props.labellingParent) {
			this.props.labellingParent.onFocusLost();
		}
	};
	protected getMaxlength(): number {
		return this.props.maxLength as number;
	}
	protected doAfterCheckCompleted(): this {
		if (this.props.labellingParent) {
			this.props.labellingParent.onCheckCompleted(!this.isCheckFailOnWhatever());
		}
		return this;
	}
	protected setValueToDOM(value: any, propName?: string): this {
		if (propName == null || propName === this.getPropName()) {
			this.toggleNumberRef.current.value = value;
		}
		return this;
	}
	protected isEditable(): boolean {
		const props = this.props as any;
		return !(props.readonly || props.disabled);
	}
	private renderMinusButton(): JSX.Element {
		return <div onClick={this.minusNumber}>-</div>;
	}
	private renderAddButton(): JSX.Element {
		return <div onClick={this.addNumber}>+</div>;
	}
	private minusNumber = () => {
		let value = this.toggleNumberRef.current.value;
		if (Number(value) > this.minValue) {
			value = Number(value) - 1;
			this.toggleNumberRef.current.value = value;
		} else {
			return;
		}
		this.setValueToModel(value).checkData();
	}
	private addNumber = () => {
		let value = this.toggleNumberRef.current.value;
		if (Number(value) < this.maxValue) {
			value = Number(value) + 1;
			this.toggleNumberRef.current.value = value;
		} else {
			return;
		}
		this.setValueToModel(value).checkData();
	}
	focus(): this {
		this.toggleNumberRef.current.focus();
		return this;
	}
	protected handleValueChange(delayFunc: () => void): void {
		if (this.changeDelayHandler) {
			clearTimeout(this.changeDelayHandler);
			delete this.changeDelayHandler;
		}
		this.changeDelayHandler = setTimeout(() => {
			delayFunc.call(this);
			delete this.changeDelayHandler;
		}, 300);
	}
}

DeskComponentRegistry.register({
	toggleNumber: DeskToggleNumber
});

export { DeskToggleNumber };
